import { isEmpty, isUndefined } from 'lodash'

import { format } from '../../components/Currency'
import {
  createDateFromMMDDYYYY,
  createDateFromYYYYMMDD,
  createMMDDYYYYFromYYYYMMDD,
} from './DateUtils'
import * as utils from './GeneralUtils'
import { getIntlObj, simpleTranslate, translate } from './TranslationUtils'
import { MMDDYYYY } from './formatDate'
import { isDayAfter } from './isDayAfter'
import { isDayBefore } from './isDayBefore'

export const MAX_CHARS_DEFAULT = 100
export const MAX_CHARS_POSTAL_CODE = 20
export const MIN_CHARS_POSTAL_CODE = 2

// NOTE - DO NOT ADD any more messages here.
// If you need to add messages move your code out
// of this external library. Current messages are
// just to keep the code refactor issues down.
const defaultIntlObj = {
  locale: 'en-US',
  messages: {
    'external.errors.fields.value.required': 'Required',
    'external.errors.fields.value.required.date': 'Missing or invalid input',
    'external.errors.fields.numeric.range':
      'Value must be between {minimum} and {maximum}',
    'external.errors.fields.numeric.range.same': 'Value can only be {minimum}',
    'external.errors.fields.numeric.greater.than':
      'Value must be greater than or equal to {minimum}',
    'external.errors.fields.numeric.less.than':
      'Value must be less than or equal to {maximum}',
    'external.errors.fields.numeric.decimalplaces':
      'Max {maxDecimalPlaces} decimal places allowed',
    'external.errors.fields.value.invalid':
      'Selection required or invalid input',
    'external.errors.fields.text.min':
      'At least {minimum} characters are required',
    'external.errors.fields.text.max':
      'No more than {maximum} characters allowed',
    'external.errors.fields.creditcard.invalid':
      'Invalid Credit Card number. Please re-enter.',
    'external.errors.fields.email.invalid': 'Invalid Email. Please re-enter.',
    'external.errors.fields.phone.invalid':
      'Invalid Phone number. Please re-enter.',
    'external.errors.fields.confirm.invalid':
      'Entered values do not match. Please re-enter.',
    'external.errors.fields.zip.invalid':
      'Please enter a valid zip/postal code.',
    'external.errors.fields.date.less.than': 'Latest date allowed is {maxDate}',
    'external.errors.fields.date.greater.than':
      'Earliest date allowed is {minDate}',
    'external.errors.fields.time.less.than': 'Latest time allowed is {maxTime}',
    'external.errors.fields.time.greater.than':
      'Earliest time allowed is {minTime}',
    'external.time.AM': 'AM',
    'external.time.PM': 'PM',
  },
}

export function validateIfValue(intlObj, validateObj = {}) {
  if (validateObj.value !== undefined && !utils.isEmpty(validateObj.value)) {
    return validate(intlObj, validateObj)
  }

  return []
}

export function validate(
  intlObj,
  {
    value = null,
    required = false,
    type = 'text',
    minChars = null,
    maxChars = null,
    minNum = null,
    maxNum = null,
    isCurrency = false,
    isCreditCard = false,
    isCVV = false,
    isEmail = false,
    isPhone = false,
    isConfirm = false,
    comparedValue = null,
    acceptableValues = null,
    isZip = false,
    countryCode,
    allowDecimal = false,
    maxDecimalPlaces = 0,
    isDateLiteral = false,
    minDateLiteral = null,
    maxDateLiteral = null,
    minTimeLiteral = null,
    maxTimeLiteral = null,
    use24HourFormatForMsgs = false,
  },
) {
  const errors = []
  if (required && !isDateLiteral) {
    addErrors(validateMandatoryStringOrDate(intlObj, value), errors)
  }
  if (required && isDateLiteral) {
    addErrors(validateMandatoryDateLiteral(intlObj, value), errors)
  }
  if (acceptableValues !== null) {
    addErrors(
      validateAcceptableValues(intlObj, value, acceptableValues),
      errors,
    )
  }
  if (maxChars !== null) {
    addErrors(validateMaxChars(intlObj, value, maxChars), errors)
  }
  if (minChars !== null) {
    addErrors(validateMinChars(intlObj, value, minChars), errors)
  }
  if (isCreditCard) {
    addErrors(validateCreditCard(intlObj, value), errors)
  }
  if (isCVV) {
    addErrors(validateCVV(intlObj, value), errors)
  }
  if (isEmail || type === 'email') {
    addErrors(validateEmail(intlObj, value), errors)
  }
  if (isPhone || type === 'tel') {
    addErrors(validatePhone(intlObj, value), errors)
  }
  if (isConfirm) {
    addErrors(validateConfirm(intlObj, value, comparedValue), errors)
  }
  if (minNum !== null && maxNum !== null) {
    addErrors(
      validateNumericRange(intlObj, value, minNum, maxNum, isCurrency),
      errors,
    )
  } else {
    if (minNum !== null) {
      addErrors(validateMinNumber(intlObj, value, minNum, isCurrency), errors)
    }
    if (maxNum !== null) {
      addErrors(validateMaxNumber(intlObj, value, maxNum, isCurrency), errors)
    }
  }

  if (allowDecimal) {
    addErrors(validateDecimalNumber(intlObj, value, maxDecimalPlaces), errors)
  }
  if (isZip) {
    addErrors(validateZip(intlObj, value, countryCode), errors)
  }
  if (minDateLiteral !== null && isDateLiteral) {
    addErrors(validateMinDateLiteral(intlObj, value, minDateLiteral), errors)
  }

  if (maxDateLiteral !== null && isDateLiteral) {
    addErrors(validateMaxDateLiteral(intlObj, value, maxDateLiteral), errors)
  }

  if (minTimeLiteral !== null && isDateLiteral) {
    addErrors(
      validateMinTimeLiteral(
        intlObj,
        value,
        minTimeLiteral,
        use24HourFormatForMsgs,
      ),
      errors,
    )
  }

  if (maxTimeLiteral !== null && isDateLiteral) {
    addErrors(
      validateMaxTimeLiteral(
        intlObj,
        value,
        maxTimeLiteral,
        use24HourFormatForMsgs,
      ),
      errors,
    )
  }

  return errors
}

export function addErrors(result, errors) {
  if (!utils.isEmpty(result) && result.message !== null) {
    errors.push(result.message)
  }
}

function getValidRet() {
  return {
    valid: true,
    message: null,
  }
}

export function validateMandatoryStringOrDate(intlObj, value) {
  const ret = getValidRet()
  const isValid = isValidMandatoryStringOrDate(value)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.value.required',
      null,
      null,
      'Error',
    )
  }

  return ret
}

export function validateMandatoryDateLiteral(intlObj, value) {
  const ret = getValidRet()
  const isValid = isValidMandatoryDateLiteral(value)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.value.required.date',
      null,
      null,
      'Error',
    )
  }

  return ret
}

export function validateAcceptableValues(intlObj, value, acceptableValues) {
  const ret = getValidRet()
  const isValid = isAcceptableValue(value, acceptableValues)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.value.invalid',
      null,
      null,
      'Error',
    )
  }

  return ret
}

export function validateMaxChars(intlObj, value, maxChars) {
  const ret = getValidRet()
  const isValid = isValidMaxChars(value, maxChars)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.text.max',
      { maximum: maxChars },
      null,
      'Error',
    )
  }

  return ret
}

export function validateMinChars(intlObj, value, minChars) {
  const ret = getValidRet()
  const isValid = isValidMinChars(value, minChars)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.text.min',
      { minimum: minChars },
      null,
      'Error',
    )
  }

  return ret
}

export function validateNumericRange(
  intlObj,
  value,
  minNum,
  maxNum,
  isCurrency = false,
) {
  const ret = getValidRet()
  const isValid = isValidNumericRange(value, minNum, maxNum)
  if (!isValid) {
    const formattedMinValue = isCurrency
      ? format(minNum.toString(), '')
      : minNum
    const formattedMaxValue = isCurrency
      ? format(maxNum.toString(), '')
      : maxNum
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      minNum === maxNum
        ? 'external.errors.fields.numeric.range.same'
        : 'external.errors.fields.numeric.range',
      {
        minimum: formattedMinValue,
        maximum: formattedMaxValue,
      },
      null,
      'Error',
    )
  }

  return ret
}

export function validateMinNumber(intlObj, value, minNum, isCurrency = false) {
  const ret = getValidRet()
  const isValid = isValidMinNumber(value, minNum)
  if (!isValid) {
    const formattedMinValue = isCurrency
      ? format(minNum.toString(), '$$')
      : minNum
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.numeric.greater.than',
      { minimum: formattedMinValue },
      null,
      'Error',
    )
  }

  return ret
}

export function validateMaxNumber(intlObj, value, maxNum, isCurrency = false) {
  const ret = getValidRet()
  const isValid = isValidMaxNumber(value, maxNum)
  if (!isValid) {
    const formattedMaxValue = isCurrency
      ? format(maxNum.toString(), '')
      : maxNum
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.numeric.less.than',
      { maximum: formattedMaxValue },
      null,
      'Error',
    )
  }

  return ret
}

export function validateDecimalNumber(intlObj, value, maxDecimalPlaces) {
  const ret = getValidRet()
  const isValid = isValidDecimal(value, maxDecimalPlaces)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.numeric.decimalplaces',
      { maxDecimalPlaces },
      null,
      'Error',
    )
  }

  return ret
}

export function validateCreditCard(intlObj, value) {
  const isValid = isValidCreditCard(value)
  const inValidCreditCardMsg = simpleTranslate(
    getIntlObj(intlObj, defaultIntlObj),
    'external.errors.fields.creditcard.invalid',
    'Error',
  )
  if (!isValid) {
    return {
      valid: false,
      message: inValidCreditCardMsg,
    }
  }

  return {
    valid: true,
    message: null,
  }
}

export function validateCVV(intlObj, value) {
  const isValid = isValidCVV(value)
  const inValidCVVMsg = simpleTranslate(
    getIntlObj(intlObj, defaultIntlObj),
    'external.errors.fields.creditcard.invalid',
    'Error',
  )
  if (!isValid) {
    return {
      valid: false,
      message: inValidCVVMsg,
    }
  }

  return {
    valid: true,
    message: null,
  }
}

export function validateEmail(intlObj, value) {
  const isValid = isValidEmail(value)
  const inValidEmailMsg = simpleTranslate(
    getIntlObj(intlObj, defaultIntlObj),
    'external.errors.fields.email.invalid',
    'Error',
  )
  if (!isValid) {
    return {
      valid: false,
      message: inValidEmailMsg,
    }
  }

  return {
    valid: true,
    message: null,
  }
}

export function validatePhone(intlObj, value) {
  const isValid = isValidPhone(value)
  const invalidPhoneMsg = simpleTranslate(
    getIntlObj(intlObj, defaultIntlObj),
    'external.errors.fields.phone.invalid',
    'Error',
  )
  if (isValid) {
    return {
      valid: true,
      message: null,
    }
  }

  return {
    valid: false,
    message: invalidPhoneMsg,
  }
}

export function validateConfirm(intlObj, value, comparedValue) {
  const invalidConfirmMsg = simpleTranslate(
    getIntlObj(intlObj, defaultIntlObj),
    'external.errors.fields.confirm.invalid',
    'Error',
  )
  const isValid = isValueMatch(value, comparedValue)
  if (isValid) {
    return {
      valid: true,
      message: null,
    }
  }

  return {
    valid: false,
    message: invalidConfirmMsg,
  }
}

export function validateZip(intlObj, value, countryCode) {
  const isValid = isValidZip(value, countryCode)
  const inValidZipMsg = simpleTranslate(
    getIntlObj(intlObj, defaultIntlObj),
    'external.errors.fields.zip.invalid',
    'Error',
  )
  if (!isValid) {
    return {
      valid: false,
      message: inValidZipMsg,
    }
  }

  return {
    valid: true,
    message: null,
  }
}

export function validateMinDateLiteral(intlObj, value, minDateLiteral) {
  const ret = getValidRet()
  const isValid = isValidMinDateLiteral(value, minDateLiteral)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.date.greater.than',
      { minDate: createMMDDYYYYFromYYYYMMDD(minDateLiteral, '-', '-') },
      null,
      'Error',
    )
  }

  return ret
}

export function validateMaxDateLiteral(intlObj, value, maxDateLiteral) {
  const ret = getValidRet()
  const isValid = isValidMaxDateLiteral(value, maxDateLiteral)
  if (!isValid) {
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.date.less.than',
      { maxDate: createMMDDYYYYFromYYYYMMDD(maxDateLiteral, '-', '-') },
      null,
      'Error',
    )
  }

  return ret
}

export function validateMinTimeLiteral(
  intlObj,
  value,
  minTimeLiteral,
  use24HourFormatForMsgs,
) {
  const ret = getValidRet()
  const isValid = isValidMinTimeLiteral(value, minTimeLiteral)
  if (!isValid) {
    let minTime = minTimeLiteral
    if (!use24HourFormatForMsgs) {
      const amLabel = translate(
        getIntlObj(intlObj, defaultIntlObj),
        'external.time.AM',
      )
      const pmLabel = translate(
        getIntlObj(intlObj, defaultIntlObj),
        'external.time.PM',
      )
      minTime = createAMPMTimeLabelFromTimeLiteral(
        minTimeLiteral,
        amLabel,
        pmLabel,
      )
    }
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.time.greater.than',
      { minTime },
      null,
      'Error',
    )
  }

  return ret
}

export function validateMaxTimeLiteral(
  intlObj,
  value,
  maxTimeLiteral,
  use24HourFormatForMsgs,
) {
  const ret = getValidRet()
  const isValid = isValidMaxTimeLiteral(value, maxTimeLiteral)
  if (!isValid) {
    let maxTime = maxTimeLiteral
    if (!use24HourFormatForMsgs) {
      const amLabel = translate(
        getIntlObj(intlObj, defaultIntlObj),
        'external.time.AM',
      )
      const pmLabel = translate(
        getIntlObj(intlObj, defaultIntlObj),
        'external.time.PM',
      )
      maxTime = createAMPMTimeLabelFromTimeLiteral(
        maxTimeLiteral,
        amLabel,
        pmLabel,
      )
    }
    ret.valid = false
    ret.message = translate(
      getIntlObj(intlObj, defaultIntlObj),
      'external.errors.fields.time.less.than',
      { maxTime },
      null,
      'Error',
    )
  }

  return ret
}

/* --------------------Validation Functions----------------------------- */
export function isValidMandatoryStringOrDate(value) {
  let valid = false
  if (!utils.isEmpty(value)) {
    valid = true
  } else if (utils.isDate(value)) {
    valid = true
  }

  return valid
}

export function isValidMandatoryDateLiteral(value) {
  // only checking for empty value right now as all date components will nullify incorrect date values
  let valid = false
  if (!utils.isEmpty(value)) {
    valid = true
  }

  return valid
}

export function isAcceptableValue(value, acceptableValues) {
  let valid = false
  if (acceptableValues !== undefined && acceptableValues.length > 0) {
    for (const acceptableValue of acceptableValues) {
      if (value === acceptableValue) {
        valid = true
      }
    }
  }

  return valid
}

export function isValidMinChars(value, minChars) {
  if (utils.isEmpty(value) || value.length < minChars) {
    return false
  }

  return true
}

export function isValidMaxChars(value, maxChars) {
  let valid = true
  if (!utils.isEmpty(value)) {
    if (value.length > maxChars) {
      valid = false
    }
  }

  return valid
}

export function isValidNumber(value) {
  if (!utils.isEmpty(value) && !isNaN(value)) {
    return true
  }

  return false
}

export function isValidNumericRange(value, min, max) {
  if (isValidMinNumber(value, min) && isValidMaxNumber(value, max)) {
    return true
  }

  return false
}

export function isValidMinNumber(value, min) {
  let valid = false
  if (isValidNumber(value)) {
    if (value >= min) {
      valid = true
    }
  }

  return valid
}

export function isValueMatch(value, matchValue) {
  if (value === matchValue) {
    return true
  }

  return false
}

export function isValidMaxNumber(value, max) {
  let valid = false
  if (isValidNumber(value)) {
    if (value <= max) {
      valid = true
    }
  }

  return valid
}

export function isValidDecimal(value, maxDecimalPlaces = 0) {
  let valid = false
  const decimalPlaces = utils.getDecimalPlacesFromNumString(
    value,
    maxDecimalPlaces,
  )

  if (decimalPlaces <= maxDecimalPlaces) {
    valid = true
  }

  return valid
}

export function isValidCreditCard(value) {
  // accept only digits, dashes or spaces
  if (/[^0-9-\s]+/.test(value)) {
    return false
  }

  // https://en.wikipedia.org/wiki/Payment_card_number
  if (value.length < 12) {
    return false
  }

  // The Luhn Algorithm
  let nCheck = 0
  let nDigit = 0
  let bEven = false
  value = value.replace(/\D/g, '')

  for (let i = value.length - 1; i >= 0; i--) {
    const cDigit = value.charAt(i)
    nDigit = parseInt(cDigit, 10)

    if (bEven) {
      if ((nDigit *= 2) > 9) {
        nDigit -= 9
      }
    }

    nCheck += nDigit
    bEven = !bEven
  }

  return nCheck % 10 === 0
}

export function isValidCVV(value) {
  // accept only digits, dashes or spaces
  if (/^[0-9]{3,4}$/.test(value)) {
    return true
  }

  return false
}

export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export function isValidEmail(email) {
  const re = EMAIL_REGEX

  return re.test(email)
}

export const PHONE_REGEX =
  /^[+]*([0-9]{1,3})?[-. ]*\(?([0-9]{2,3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4,5})$/

export function isValidPhone(value) {
  const phoneno = PHONE_REGEX
  // let phoneno = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im
  if (value && value.match(phoneno)) {
    return true
  }

  return false
}

export const zipRegexes = {
  USA: /^\d{5}(?:[-\s]\d{4})?$/,
  CAN: /[ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ] ?[0-9][ABCEGHJKLMNPRSTVWXYZ][0-9]/,
}

export function isValidZip(zip, countryCode) {
  zip = zip ? zip.toUpperCase() : zip
  const zipRe = zipRegexes[countryCode]

  return !zipRe || zipRe.test(zip)
}

export function isValidMinDateLiteral(value, minDateLiteral) {
  let valid = true
  const date = createDateFromYYYYMMDD(value)
  const minDate = createDateFromYYYYMMDD(minDateLiteral)
  // console.log(value, minDateLiteral)
  // console.log(date, minDate)
  if (isDayBefore(date, minDate)) {
    valid = false
  }

  return valid
}

export function isValidMaxDateLiteral(value, maxDateLiteral) {
  let valid = true
  const date = createDateFromYYYYMMDD(value)
  const maxDate = createDateFromYYYYMMDD(maxDateLiteral)
  if (isDayAfter(date, maxDate)) {
    valid = false
  }

  return valid
}

export function isValidMinTimeLiteral(value, minTimeLiteral) {
  let valid = true
  if (isBeforeTimeLiteral(value, minTimeLiteral)) {
    valid = false
  }

  return valid
}

export function isValidMaxTimeLiteral(value, maxTimeLiteral) {
  let valid = true
  if (isAfterTimeLiteral(value, maxTimeLiteral)) {
    valid = false
  }

  return valid
}

export function isValidDateLiteral(dateLiteral, format = MMDDYYYY) {
  let isValid = false
  if (format === MMDDYYYY) {
    const re = /^((0|1)\d{1})-((0|1|2)\d{1})-((19|20)\d{2})/g
    if (re.test(dateLiteral)) {
      try {
        const date = createDateFromMMDDYYYY(dateLiteral)
        if (date instanceof Date && !isNaN(date)) {
          isValid = true
        }
      } catch (e) {
        console.error('not valid date literal', e)
      }
    }
  }

  return isValid
}

export function getHourFromTimeLIteral(time, asNumber = false) {
  if (!isUndefined(time) && !isEmpty(time)) {
    const timeArr = time.split(':')
    if (asNumber) {
      return parseInt(timeArr[0])
    }

    return timeArr[0]
  }

  return null
}

export function getMinuteFromTimeLIteral(time, asNumber = false) {
  if (!isUndefined(time) && !isEmpty(time)) {
    const timeArr = time.split(':')
    if (asNumber) {
      return parseInt(timeArr[1])
    }

    return timeArr[1]
  }

  return null
}

export function isBeforeTimeLiteral(time1, time2) {
  const time1Hour = getHourFromTimeLIteral(time1, true)
  const time1Minute = getMinuteFromTimeLIteral(time1, true)
  const time2Hour = getHourFromTimeLIteral(time2, true)
  const time2Minute = getMinuteFromTimeLIteral(time2, true)

  let isTimeBefore = false

  if (
    !isUndefined(time1) &&
    !isEmpty(time1) &&
    !isUndefined(time2) &&
    !isEmpty(time2)
  ) {
    if (time1Hour < time2Hour) {
      isTimeBefore = true
    } else if (time1Hour === time2Hour && time1Minute < time2Minute) {
      isTimeBefore = true
    }
  }

  return isTimeBefore
}

export function isAfterTimeLiteral(time1, time2) {
  const time1Hour = getHourFromTimeLIteral(time1, true)
  const time1Minute = getMinuteFromTimeLIteral(time1, true)
  const time2Hour = getHourFromTimeLIteral(time2, true)
  const time2Minute = getMinuteFromTimeLIteral(time2, true)

  let isTimeAfter = false

  if (
    !isUndefined(time1) &&
    !isEmpty(time1) &&
    !isUndefined(time2) &&
    !isEmpty(time2)
  ) {
    if (time1Hour > time2Hour) {
      isTimeAfter = true
    } else if (time1Hour === time2Hour && time1Minute > time2Minute) {
      isTimeAfter = true
    }
  }

  return isTimeAfter
}

export function createAMPMTimeLabelFromTimeLiteral(
  time,
  amLabel = 'AM',
  pmLabel = 'PM',
  separator = ':',
) {
  if (isUndefined(time) || isEmpty(time)) {
    return ''
  }

  const ampmMinute = getMinuteFromTimeLIteral(time)

  let hour = getHourFromTimeLIteral(time, true)
  let ampmHour = getHourFromTimeLIteral(time)
  let ampm = amLabel

  if (hour === 12) {
    ampm = pmLabel
  }

  if (hour > 12) {
    ampm = pmLabel
    hour -= 12
    ampmHour = hour.toString()
  }

  return `${ampmHour + separator + ampmMinute} ${ampm}`
}
